import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const RouterContext = createContext(null);

function browserLocation(defaultPath = "/") {
  const initialPath = window.location.pathname || "/";
  if (initialPath === "/" && defaultPath !== "/") {
    window.history.replaceState({}, "", defaultPath);
  }
  return {
    path:
      initialPath === "/" && defaultPath !== "/"
        ? defaultPath
        : initialPath,
    search: window.location.search || "",
    hash: window.location.hash || "",
  };
}

export function RouterProvider({
  children,
  defaultPath = "/",
  scrollBehavior = "auto",
}) {
  const [location, setLocation] = useState(() =>
    browserLocation(defaultPath),
  );

  useEffect(() => {
    const onPopState = () => setLocation(browserLocation("/"));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const value = useMemo(() => ({
    ...location,
    navigate(to, { replace = false, scroll = true, state = {} } = {}) {
      const target = new URL(to, window.location.origin);
      const current = `${location.path}${location.search}${location.hash}`;
      const next = `${target.pathname}${target.search}${target.hash}`;
      if (next === current) return;
      window.history[replace ? "replaceState" : "pushState"](state, "", to);
      setLocation({
        path: target.pathname,
        search: target.search,
        hash: target.hash,
      });
      if (scroll) {
        window.scrollTo({ top: 0, behavior: scrollBehavior });
      }
    },
  }), [location, scrollBehavior]);

  return (
    <RouterContext.Provider value={value}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  const router = useContext(RouterContext);
  if (!router) {
    throw new Error("useRouter debe usarse dentro de RouterProvider.");
  }
  return router;
}

export function AppLink({
  to,
  className = "",
  activeClassName = "active",
  exact = false,
  state,
  replace = false,
  children,
  onClick,
  ...props
}) {
  const { path, navigate } = useRouter();
  const active = exact || to === "/"
    ? path === to
    : path.startsWith(to);
  return (
    <a
      {...props}
      href={to}
      className={`${className} ${active ? activeClassName : ""}`.trim()}
      onClick={(event) => {
        if (
          event.defaultPrevented
          || event.button !== 0
          || event.metaKey
          || event.ctrlKey
          || event.shiftKey
          || event.altKey
        ) return;
        event.preventDefault();
        onClick?.(event);
        navigate(to, { state, replace });
      }}
    >
      {children}
    </a>
  );
}
